import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
});

const TOKEN_KEY = "onsetflow_token";
const _ROLE_CACHE_KEY = "osf:project-role-cache";
const _projectRoleMemory = new Map();

/**
 * Iter-158.12.11 — clear the project-role cache. Called on
 * login/register/logout so account switches don't emit false
 * "You are now/no longer the project owner" toasts by comparing
 * one user's cached role against another user's role on the same
 * project id (sessionStorage survives tab reloads and re-logins
 * unless we clear it).
 */
function _clearProjectRoleCache() {
    _projectRoleMemory.clear();
    try {
        sessionStorage.removeItem(_ROLE_CACHE_KEY);
    } catch { /* private mode etc. */ }
}

export const tokenStore = {
    get: () => localStorage.getItem(TOKEN_KEY),
    set: (t) => {
        // Reset the role-change cache whenever a NEW token is set —
        // covers login, register, and password reset (which all issue
        // a fresh token). Any cached role belongs to the previous
        // user and would produce a false "role changed" toast on the
        // first project fetch by the new user.
        const prev = localStorage.getItem(TOKEN_KEY);
        if (prev !== t) {
            _clearProjectRoleCache();
        }
        localStorage.setItem(TOKEN_KEY, t);
    },
    clear: () => {
        _clearProjectRoleCache();
        localStorage.removeItem(TOKEN_KEY);
    },
};

api.interceptors.request.use((config) => {
    const token = tokenStore.get();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Iter-156A.4 Phase 4 — global parity toast for archive-lock 403s.
//
// Phase 1's middleware emits {code: "project_archived", detail: "..."}
// for every write blocked by the archive lock. Some callers already
// render the calm copy inline (e.g. the AddCastMember dialog uses the
// formatApiError shim to surface it as a red banner). For list-view
// writes that have NO host dialog (e.g. Flag create from /flags, scene
// inline edit, photo upload from gallery) the caller may only set a
// local error state OR swallow the error — leaving the user without a
// clear explanation of why their click didn't work.
//
// This interceptor surfaces the calm copy as ONE toast per 5-second
// window across the whole app. Coalescing avoids duplicate toasts
// when a single user action fires multiple parallel writes (e.g.
// bulk operations).
let _lastArchiveToastAt = 0;
// Iter-157D — global permission-change broadcast. When the backend
// returns 403 with code=insufficient_role (or any other 403 with a
// "you don't have permission" detail), broadcast a CustomEvent so
// the project-level page can refetch its cached `project.role` and
// re-render UI gates immediately. Throttled to once per 4s so a
// burst of 403s from a bulk operation doesn't toast-bomb the user.
let _lastPermToastAt = 0;
// Iter-157E P0(6) — Permission propagation. We additionally watch
// every successful GET to /projects/{id} (any page that fetches
// the project payload — ProjectHome, ProjectSettings, ScenesHub,
// CastHub, SceneDetail, OnSetMode, PrepMode, etc.) and compare
// the returned `role` to the last-seen role for that project.
// When the role transitions, we broadcast `osf:role-changed` so a
// global listener can toast the user — even if their current
// page doesn't subscribe to the permissions-stale event. This
// closes the "I'm on SceneDetail and got promoted by an admin in
// another tab and never noticed until my next page navigation"
// gap. Stored in sessionStorage so the comparison survives soft
// navigations but doesn't survive tab close (we don't want stale
// values leaking across re-logins).
//
// Iter-158.12.11 — _ROLE_CACHE_KEY + _projectRoleMemory + the
// _clearProjectRoleCache helper are declared above (near tokenStore)
// so login/logout/register can flush the cache and account switches
// don't fire cross-user false "You are now/no longer the project
// owner" toasts.
function _projectRoleCacheGet(pid) {
    if (_projectRoleMemory.has(pid)) return _projectRoleMemory.get(pid);
    try {
        const blob = sessionStorage.getItem(_ROLE_CACHE_KEY);
        if (!blob) return null;
        const obj = JSON.parse(blob);
        return obj?.[pid] || null;
    } catch {
        return null;
    }
}
function _projectRoleCacheSet(pid, role) {
    _projectRoleMemory.set(pid, role);
    try {
        const blob = sessionStorage.getItem(_ROLE_CACHE_KEY);
        const obj = blob ? JSON.parse(blob) : {};
        obj[pid] = role;
        sessionStorage.setItem(_ROLE_CACHE_KEY, JSON.stringify(obj));
    } catch { /* private mode etc. */ }
}
const _PROJECT_GET_RE = /\/projects\/([^/?]+)(?:\?|$)/;
// Iter-157I P2 — Event consistency. Mutation responses on these
// resource patterns broadcast a global event so any FE surface
// caching the affected count or list can refetch without manual
// page reload. Mirrors the `osf:scripts-changed` pattern from
// Iter-157H. Low-cost central interceptor; no per-page wiring.
const _FLAGS_RE = /\/projects\/([^/?]+)\/flags(?:\/|$)/;
const _MEMBERS_RE = /\/projects\/([^/?]+)\/members(?:\/|$)/;
api.interceptors.response.use(
    (resp) => {
        // Only inspect 2xx GET responses; never throw from this
        // observability hook.
        try {
            const cfg = resp?.config || {};
            const url = cfg.url || "";
            const method = (cfg.method || "").toLowerCase();
            // Mutation broadcasts.
            if (["post", "put", "patch", "delete"].includes(method)) {
                const fm = url.match(_FLAGS_RE);
                if (fm) {
                    try {
                        window.dispatchEvent(
                            new CustomEvent("osf:flags-changed", {
                                detail: { project_id: fm[1] },
                            }),
                        );
                    } catch { /* no-op */ }
                }
                const mm = url.match(_MEMBERS_RE);
                if (mm) {
                    try {
                        window.dispatchEvent(
                            new CustomEvent("osf:members-changed", {
                                detail: { project_id: mm[1] },
                            }),
                        );
                    } catch { /* no-op */ }
                }
            }
            if (
                cfg.method === "get" &&
                resp?.data &&
                typeof resp.data === "object" &&
                typeof resp.data.role === "string" &&
                typeof resp.data.id === "string"
            ) {
                const url = cfg.url || "";
                // Match canonical /projects/{id} but NOT /projects (list)
                // or /projects/{id}/anything (nested resources).
                const m = url.match(_PROJECT_GET_RE);
                if (m && m[1] === resp.data.id) {
                    const pid = resp.data.id;
                    const nextRole = resp.data.role;
                    const prevRole = _projectRoleCacheGet(pid);
                    if (prevRole && nextRole && prevRole !== nextRole) {
                        try {
                            window.dispatchEvent(
                                new CustomEvent("osf:role-changed", {
                                    detail: {
                                        project_id: pid,
                                        previous_role: prevRole,
                                        next_role: nextRole,
                                        project_name: resp.data.name,
                                    },
                                }),
                            );
                        } catch { /* SSR / no-window */ }
                    }
                    _projectRoleCacheSet(pid, nextRole);
                }
            }
        } catch { /* never break a real response over this watcher */ }
        return resp;
    },
    (err) => {
        try {
            const status = err?.response?.status;
            const code = err?.response?.data?.code;
            const detail = err?.response?.data?.detail;
            if (status === 403 && code === "project_archived") {
                const now = Date.now();
                if (now - _lastArchiveToastAt > 5000) {
                    _lastArchiveToastAt = now;
                    import("sonner").then(({ toast }) => {
                        toast.info(
                            "This project is archived. You can still view continuity records, but editing is disabled until the project is restored.",
                        );
                    });
                }
            } else if (status === 403) {
                // Iter-157D — stale-permissions broadcast.
                // We DON'T toast here because most 403s on first
                // page load are legitimate gating (e.g. a contributor
                // hitting an owner-only endpoint), not a sign their
                // permissions just changed. The silent broadcast
                // gives pages a chance to refetch their cached
                // `project.role` without bothering the user. The
                // backend remains the authority — a stale UI gate
                // never actually grants access.
                try {
                    window.dispatchEvent(
                        new CustomEvent("osf:permissions-stale", {
                            detail: { status, code, message: (typeof detail === "string" ? detail : detail?.message) || null },
                        }),
                    );
                } catch { /* SSR/no-window safety */ }
            }
        } catch {
            /* swallow */
        }
        return Promise.reject(err);
    },
);

export function formatApiError(err) {
    // Iter-128 — Trust pass. Translate raw backend / network errors
    // into human-readable copy. Users never see "504", "ECONNABORTED",
    // "Network Error" etc. Backend `detail` strings are passed
    // through verbatim (they're already user-facing).
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const code = err?.response?.data?.code;
    // Iter-156A.4 — special-case the archive write-lock so every
    // surface gets the same calm explanation regardless of which
    // route hit the lock. Backend always emits
    // {code: "project_archived", detail: "This project is archived…"}
    // — we override the verbatim detail with longer reassurance
    // copy that explains continuity is still readable.
    if (status === 403 && code === "project_archived") {
        return "This project is archived. You can still view continuity records, but editing is disabled until the project is restored.";
    }
    if (detail != null) {
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail))
            return detail
                .map((e) =>
                    e && typeof e.msg === "string" ? e.msg : JSON.stringify(e),
                )
                .filter(Boolean)
                .join(" ");
        if (detail && typeof detail === "object") {
            // Iter-157P.12 — backend structured error objects (e.g.
            // `revision_extraction_failed`, `scene_omission_confirmation_required`)
            // carry a human-readable `message` field. Earlier code fell
            // through to `String(detail)` here which rendered as
            // "[object Object]" in toasts. Always prefer message → msg →
            // detail (nested) → a labelled JSON dump as a last resort.
            if (typeof detail.message === "string") return detail.message;
            if (typeof detail.msg === "string") return detail.msg;
            if (typeof detail.detail === "string") return detail.detail;
            if (typeof detail.error === "string") {
                // `error` is usually a machine-readable code; humanise it.
                return detail.error
                    .split("_")
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(" ");
            }
            // Should be unreachable on well-formed backend responses, but
            // never emit "[object Object]" to producers.
            try {
                return JSON.stringify(detail);
            } catch {
                return "Something went wrong. Please try again.";
            }
        }
        return String(detail);
    }
    // No structured detail from the server — translate transport-level
    // problems into something on-set crew can act on. (Iter-156A.4
    // moved `code` lookup above so the archive lock + future
    // machine-readable codes are matched before falling into this
    // generic branch.)
    const networkCode = err?.code;
    if (networkCode === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        return "The request timed out. Please check your connection and try again, or upload a smaller file.";
    }
    if (networkCode === "ERR_NETWORK" || /network/i.test(err?.message || "")) {
        return "Lost connection. Check your network and try again.";
    }
    if (status === 504 || status === 502 || status === 503) {
        return "We're still working on it — please try again in a moment.";
    }
    if (status === 413) {
        return "That file is too large. Please use a file under 10MB.";
    }
    if (status === 401) {
        return "Your session has expired. Please sign in again.";
    }
    if (status === 403) {
        return "You don't have permission to do that.";
    }
    if (status === 404) {
        return "We couldn't find what you were looking for.";
    }
    if (status && status >= 500) {
        return "We're still working on it. Please try again in a moment.";
    }
    return err?.message || "We're still working on it. Please try again in a moment.";
}
